/**
 * Las fotos de los productos, en JPEG para un PDF.
 *
 * A PDF draws a JPEG as it is and knows nothing of WebP, which is what most photos here are kept as
 * (`fotos.ts`). So the catalogue's photos are decoded and written again, small, on this device and
 * from the bytes it already holds: nothing is asked of the server, and a photo this device does not
 * have yet is left out of the file rather than waited for.
 */

import { db } from '../db/dexie.js';
import type { Jpeg } from './pdf.js';

/**
 * Pixels a side: half an inch on paper at about three hundred to the inch, and sharp enough on a
 * phone zoomed in on a page. A few kilobytes each.
 */
export const LADO_JPEG = 144;

/** Each photo once a page load: opening the catalogue again, or with another list, costs nothing. */
const hechas = new Map<string, Promise<Jpeg | null>>();

/** The photo named `huella` as a JPEG, or null when this device does not hold it or cannot read it. */
export function jpegDeFoto(huella: string): Promise<Jpeg | null> {
  let hecha = hechas.get(huella);
  if (!hecha) {
    hecha = convertir(huella).catch((e: unknown) => {
      console.warn('[catálogo] la foto no se pudo poner', huella, e);
      return null;
    });
    hechas.set(huella, hecha);
    // One not here yet may be by next time: a sync brings every product's photo down.
    void hecha.then((j) => {
      if (!j) hechas.delete(huella);
    });
  }
  return hecha;
}

async function convertir(huella: string): Promise<Jpeg | null> {
  const fila = await db.fotos.get(huella);
  if (!fila) return null;
  const bmp = await createImageBitmap(fila.datos);
  try {
    const lado = Math.min(LADO_JPEG, Math.max(bmp.width, bmp.height));
    const lienzo = document.createElement('canvas');
    lienzo.width = lado;
    lienzo.height = lado;
    const ctx = lienzo.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // White under it, as the paper is; and whole, should a photo from before not be square.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, lado, lado);
    const escala = lado / Math.max(bmp.width, bmp.height);
    const ancho = bmp.width * escala;
    const alto = bmp.height * escala;
    ctx.drawImage(bmp, (lado - ancho) / 2, (lado - alto) / 2, ancho, alto);
    const blob = await new Promise<Blob | null>((listo) => lienzo.toBlob(listo, 'image/jpeg', 0.82));
    if (!blob || blob.type !== 'image/jpeg') return null;
    return { datos: new Uint8Array(await blob.arrayBuffer()), ancho: lado, alto: lado };
  } finally {
    bmp.close();
  }
}
